import { AnimatedVisibility } from "../AnimatedVisibility";

// Default icon for debugging.
const RED_CROSS_ICON = '<svg width="15" height="15" style="display: block" xmlns="http://www.w3.org/2000/svg"><path stroke-width="1" stroke="red" d="M0 7 L15 7 M7 0 L7 15"/></svg>';

// Where should the content be anchored relative to the 2D canvas position?
export const CONTENT_ANCHORING = Object.freeze({ START: 's', BOTTOM_CENTER: 'bc', CENTER: 'c', END: 'e' });

/** A UI element that is positioned in 2D space, likely based on a 3D position may be tracking translations. */
export class PositionedUI {
  constructor(
  hud,
  layerID,
  position,
  content = RED_CROSS_ICON,
  anchor = CONTENT_ANCHORING.CENTER,
  VisibilityClass = AnimatedVisibility)
  {
    this.hud = hud;

    this.dom = createOuterHostDom(hud.getDocument());
    this.innerDom = createInnerHostDom(hud.getDocument());
    this.visibility = new VisibilityClass(this.dom, false);
    this.position = position;
    // listens and react to change in the 2D canvas position. (and pre-updates for the first collision targets)
    this.position.updateCanvasPosition(this.hud.viewer);
    this.position.addCanvas2DListener(this);
    this.hasUnappliedPosition = true;

    this.setContent(content);
    this.setContentAnchor(anchor);
    this.dom.appendChild(this.innerDom);

    // Plugins to extend the functionality of this UI element.
    this.plugins = [];
    // Dictionary of user data.
    this.data = {};
    // Dimensions of rendered UI element.
    this.dimensions = null;
  }

  onCanvas2DChange({ x, y }, forced) {
    if (this.isVisible() || forced) {
      this.dom.style.translate = `${x}px ${y}px`;
      this.hasUnappliedPosition = false;
      return;
    }

    this.hasUnappliedPosition = true;
  }

  init(container) {
    for (const p of this.plugins) p.init?.(this);
    container.appendChild(this.dom);
  }

  dispose() {
    return this.visibility.turnOff().
    then(() => {
      for (const p of this.plugins) p.dispose?.(this);
      this.dom.remove();
    });
  }

  setContentAnchor(anchor) {
    this.anchor = anchor;
    setTranslationFromAnchor(this.innerDom, this.anchor);
  }

  // Either html content or a DOM element.
  setContent(content) {
    if (typeof content === 'string') {
      this.innerDom.innerHTML = content;
    } else {
      this.innerDom.appendChild(content);
    }
  }

  isVisible() {
    return this.visibility.valueOf();
  }

  setVisible(visible) {
    // We delayed resolving DOM position until the UI is visible.
    if (visible && this.hasUnappliedPosition) {
      this.onCanvas2DChange(this.position.cached2D, true);
    }

    this.visibility.set(visible);

    if (!this.dimensions) {
      this.updateDimensionsOnNextFrame();
    }
  }

  #updateDimensions() {
    if (this.isVisible()) {
      const target = this.collisionTarget ?? this.innerDom;
      this.dimensions = {
        width: target.clientWidth,
        height: target.clientHeight
      };
    } else {
      // For invisible labels, this causes dimensions to be invalidated and estimated size to be used
      this.dimensions = null;
    }
  }

  updateDimensionsOnNextFrame() {
    requestAnimationFrame(() => requestAnimationFrame(this.#updateDimensions.bind(this)));
  }
}

function createInnerHostDom(document) {
  const inner = document.createElement('div');
  inner.style.width = 'max-content';

  return inner;
}

function createOuterHostDom(document) {
  const outer = document.createElement('div');
  const s = outer.style;
  s.position = 'absolute';
  s.left = '0';
  s.top = '0';
  s.width = '0';
  s.height = '0';
  s.willChange = 'transform'; // add opacity if needed.

  return outer;
}

function setTranslationFromAnchor(dom, anchor) {
  // Adds separation with the position for horizontal anchors. Ideal would be to use `translate` only, but padding
  // and similar techniques would add unwanted event capture area.
  switch (anchor) {
    case CONTENT_ANCHORING.START:
      return dom.style.transform = 'translate(10px, -50%)';
    case CONTENT_ANCHORING.END:
      return dom.style.transform = 'translate(calc(-10px - 100%), -50%)';
    case CONTENT_ANCHORING.BOTTOM_CENTER:
      return dom.style.transform = 'translate(-50%, -100%)';
    case CONTENT_ANCHORING.CENTER:
    default:
      return dom.style.transform = 'translate(-50%, -50%)';
  }
}

/**
 * Computes screen space boundaries of a UI based on PositionedUI's latest 2D position.
 * NOTE: this assumes the UI is entirely positioned according to the anchor.
 * @param {{ width, height }} size
 * @param {{x, y}} canvas2D
 * @param {CONTENT_ANCHORING} anchor
 * @param [dst]
 * @return {{x, y, width, height}}
 */
export function getLatestClientRect(size, canvas2D, anchor, dst = {}) {
  const { width, height } = size;
  const { x, y } = canvas2D;
  dst.width = width;
  dst.height = height;
  dst.x = x;
  dst.y = y;

  // NOTE must match setTranslationFromAnchor
  switch (anchor) {
    case CONTENT_ANCHORING.START:
      dst.x += 10;
      dst.y -= height * 0.5;
      return dst;
    case CONTENT_ANCHORING.END:
      dst.x -= 10 + width;
      dst.y -= height * 0.5;
      return dst;
    case CONTENT_ANCHORING.BOTTOM_CENTER:
      dst.x -= width * 0.5;
      dst.y -= height;
      return dst;
    case CONTENT_ANCHORING.CENTER:
    default:
      dst.x -= width * 0.5;
      dst.y -= height * 0.5;
      return dst;
  }
}